import { useMemo } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Icon } from '@antimatterhq/ui/components/icon';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Button } from '@antimatterhq/ui/components/button';
import { Label } from '@antimatterhq/ui/components/label';
import { Link } from '@antimatterhq/ui/components/typography';
import { Section } from '@/components/container/section/Section';
import { AddDetectionRuleSchemaValues } from '@/zod/schemas/detectionRules/AddDetectionRule';
import { useGetWorkspaceConfig } from '@/hooks/workspace/useGetWorkspaceConfig';
import {
  observableConfidenceTooltip,
  observableImpactTooltip,
  observableRelationshipTooltip,
  observableValueTooltip,
} from '@/strings/StringsTooltips';
import { FormInput } from '@/components/form/input/FormInput';
import {
  FormCodeEditor,
  FormDropdown,
  FormError,
} from '@/components/form/form';
import { useParentUrl } from '@/hooks/iframe/useParentUrl';

export const ObservablesSection = () => {
  const { data: workspaceConfig } = useGetWorkspaceConfig();
  const { values, handleChange, handleBlur } =
    useFormikContext<AddDetectionRuleSchemaValues>();
  const parentUrl = useParentUrl();

  const kindOptions = useMemo(() => {
    return (
      workspaceConfig?.spec.observables?.kinds?.map((kind) => ({
        name: kind.name ?? 'Unknown',
        value: kind.name ?? 'Unknown',
      })) ?? []
    );
  }, [workspaceConfig]);

  const relationshipOptions = useMemo(() => {
    return (
      workspaceConfig?.spec.observables?.relationships?.map((relationship) => ({
        name: relationship ?? 'Unknown',
        value: relationship ?? 'Unknown',
      })) ?? []
    );
  }, [workspaceConfig]);

  return (
    <>
      <FieldArray name="observables">
        {({ push, remove }) => (
          <>
            {values.observables.map((observable, index) => (
              <Section key={`observable-${index}`}>
                <Label
                  required
                  htmlFor={`observables.${index}.kind`}
                  id={`observables.${index}.kind-label`}
                >
                  Observable kind
                </Label>
                <FormDropdown
                  aria-labelledby={`observables.${index}.kind-label`}
                  name={`observables.${index}.kind`}
                  options={kindOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={observable.kind}
                  placeholder="Select an observable kind"
                  emptyState={
                    <>
                      Observable kinds can be added via the{' '}
                      <Link
                        to={`${parentUrl}/configure/workspace`}
                        target="_blank"
                      >
                        workspace config
                      </Link>
                    </>
                  }
                />
                <FormError name={`observables.${index}.kind`} />

                <Spacer size="lg" />
                <Label
                  htmlFor={`observables.${index}.value`}
                  id={`observables.${index}.value-label`}
                  info={observableValueTooltip}
                  required
                >
                  Value
                </Label>
                <FormInput
                  aria-labelledby={`observables.${index}.value-label`}
                  value={observable.value}
                  name={`observables.${index}.value`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter a value"
                />
                <FormError name={`observables.${index}.value`} />

                <Spacer size="lg" />
                <Label required info={observableRelationshipTooltip}>
                  Relationship
                </Label>
                <FormDropdown
                  name={`observables.${index}.relationship`}
                  options={relationshipOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={observable.relationship}
                  placeholder="Select a relationship"
                  emptyState={
                    <>
                      Observable relationships can be added via the{' '}
                      <Link
                        to={`${parentUrl}/configure/workspace`}
                        target="_blank"
                      >
                        workspace config
                      </Link>
                    </>
                  }
                />
                <FormError name={`observables.${index}.relationship`} />

                <Spacer size="lg" />
                <Label
                  required
                  htmlFor={`observables.${index}.risk.impact`}
                  info={observableImpactTooltip}
                  id={`observables.${index}.risk.impact-label`}
                >
                  Impact
                </Label>
                <FormCodeEditor
                  placeholder="Enter a SQL expression (e.g. src_col / 10)"
                  aria-labelledby={`observables.${index}.risk.impact-label`}
                  name={`observables.${index}.risk.impact`}
                  value={observable.risk?.impact}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="max-w-form"
                  hideControls
                />
                <FormError name={`observables.${index}.risk.impact`} />

                <Spacer size="lg" />

                <Label
                  required
                  htmlFor={`observables.${index}.risk.confidence`}
                  info={observableConfidenceTooltip}
                  id={`observables.${index}.risk.confidence-label`}
                >
                  Confidence
                </Label>
                <FormCodeEditor
                  aria-labelledby={`observables.${index}.risk.confidence-label`}
                  name={`observables.${index}.risk.confidence`}
                  placeholder="Enter a SQL expression (e.g. src_col / 5)"
                  value={observable.risk?.confidence}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="max-w-form"
                  hideControls
                />
                <FormError name={`observables.${index}.risk.confidence`} />
                <Spacer size="lg" />

                <div className="flex justify-end">
                  <Button
                    variant="default"
                    onClick={() => remove(index)}
                    data-testid={`remove-observable-button-${index}`}
                  >
                    Remove
                  </Button>
                </div>
              </Section>
            ))}
            {!!values.observables.length && <Spacer size="lg" />}
            <Button
              // Push a new observable with empty values
              onClick={() => push({ kind: '', value: '', relationship: '' })}
              data-testid="add-observable-button"
            >
              <Icon icon="lucide:plus" />
              Add observable
            </Button>
          </>
        )}
      </FieldArray>
    </>
  );
};
